import React, { useRef, useState, useEffect } from 'react'
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
} from 'recharts'
import { getAnalyticsColors, darkenColor, chartLabelStyle } from 'app/analytics'
import { AnalyticsChartWrapper } from './AnalyticsChartWrapper'

interface ChartItem {
    [key: string]: string | number
    percentage: number
}

interface CustomLabelProps {
    cx: number
    cy: number
    midAngle: number
    outerRadius: number
    percent: number
    index: number
    payload: ChartItem
}

interface TooltipProps {
    active?: boolean
    payload?: Array<{
        payload: ChartItem
    }>
}

interface PieData {
    midAngle: number
    cx: number
    cy: number
    outerRadius: number
    name: string
    percentage: number
    value: number
    index: number
    [key: string]: string | number
}

interface AnalyticsPieChartProps {
    data: ChartItem[]
    labelKey: string
    valueKey: string
    title: string
    className?: string
    compact?: boolean
    transitioning?: boolean
    smallSegmentThreshold?: number // Threshold percentage for small segments
    marginModifier?: number    // Add margin modifier prop
    onPointClick?: (payload: {
        chartType: string
        labelKey: string
        labelValue: string | number
        row: any
    }) => void
}

export function AnalyticsPieChart(props: AnalyticsPieChartProps) {
    const {
        data,
        labelKey,
        valueKey,
        title,
        className = '',
        transitioning = false,
        smallSegmentThreshold = 2, // Default 2% threshold
        onPointClick,
    } = props

    // Use refs to store segment data that persists across renders
    const segmentDataRef = useRef<Record<number, PieData>>({})
    const [smallSegments, setSmallSegments] = useState<PieData[]>([])
    const [hasRenderedLabels, setHasRenderedLabels] = useState(false)
    const [isHoveringOtherSegments, setIsHoveringOtherSegments] = useState(false)

    if (!data || data.length === 0) {
        return <div className="chart-no-data">No data available</div>
    }

    // Use deterministic colors based on chart title
    const COLORS = getAnalyticsColors(title || 'pie-chart', data.length)

    // Calculate total value for percentage calculation
    const total = data.reduce((acc, item) => acc + (Number(item[valueKey]) || 0), 0)

    // Calculate percentages for all data items upfront
    const processedData = data.map((item, index) => {
        const value = Number(item[valueKey]) || 0
        const percentage = (value / total) * 100
        return {
            ...item,
            originalIndex: index, // Store original index for color mapping
            percentage,
        }
    })

    // Pre-determine which segments are small
    useEffect(() => {
        // Reset segment data when data changes
        segmentDataRef.current = {}
        setHasRenderedLabels(false)
    }, [data, smallSegmentThreshold])

    // Process segment data after rendering
    useEffect(() => {
        if (hasRenderedLabels && Object.keys(segmentDataRef.current).length > 0) {
            const smallSegmentsData = Object.values(segmentDataRef.current)
                .filter((segment) => segment.percentage < smallSegmentThreshold)
            setSmallSegments(smallSegmentsData)
        }
    }, [hasRenderedLabels, smallSegmentThreshold])

    // Custom tooltip formatter
    const customTooltip = ({ active, payload }: TooltipProps) => {
        if (active && payload && payload.length) {
            const item = payload[0].payload
            return (
                <div className="analytics-chart-tooltip">
                    <p className="name">{item[labelKey]}</p>
                    <p className="value">{item[valueKey]}</p>
                </div>
            )
        }
        return null
    }

    // Custom label renderer with lines pointing to segments
    const renderCustomizedLabel = (labelProps: CustomLabelProps) => {
        const {
            cx,
            cy,
            midAngle,
            outerRadius,
            percent,
            index,
            payload,
        } = labelProps

        // Store this segment's data for potential aggregated label use
        const segmentInfo: PieData = {
            midAngle,
            cx,
            cy,
            outerRadius,
            name: String(payload[labelKey]),
            percentage: payload.percentage,
            value: Number(payload[valueKey]),
            index,
        }

        segmentDataRef.current[index] = segmentInfo

        // Mark that we've rendered labels once
        // This will trigger the useEffect to process small segments
        if (index === data.length - 1 && !hasRenderedLabels) {
            setTimeout(() => setHasRenderedLabels(true), 0)
        }

        // Skip rendering labels in compact mode or during transitions
        if (transitioning) return null

        // Check if this segment is below threshold
        if (payload.percentage < smallSegmentThreshold) {
            return null // These will be handled by aggregated label
        }

        // Calculate position for the line end points
        const sin = Math.sin((-midAngle * Math.PI) / 180)
        const cos = Math.cos((-midAngle * Math.PI) / 180)

        // Start point of the line (near the pie)
        const startX = cx + (outerRadius + 5) * cos
        const startY = cy + (outerRadius + 5) * sin

        // End point of the line (where the text will be)
        const textAnchor = cos >= 0 ? 'start' : 'end'
        const lineLength = 20
        const endX = startX + lineLength * cos
        const endY = startY + lineLength * sin

        // Use darkened color for better text readability
        const segmentColor = COLORS[index % COLORS.length]
        const labelColor = darkenColor(segmentColor, 30)

        return (
            <g className="animate__animated animate__fadeIn">
                <line
                    x1={startX}
                    y1={startY}
                    x2={endX}
                    y2={endY}
                    stroke={segmentColor}
                    strokeWidth={1}
                />
                <text
                    x={endX + (cos >= 0 ? 5 : -5)}
                    y={endY}
                    fill={labelColor}
                    textAnchor={textAnchor}
                    dominantBaseline="central"
                    opacity={isHoveringOtherSegments ? 0.1 : 1.0}
                    style={chartLabelStyle}
                >
                    {`${String(payload[labelKey])}: ${(percent * 100).toFixed(1)}%`}
                </text>
            </g>
        )
    }

    // Calculate the average angle for small segments
    const calculateAverageAngle = (segments: PieData[]): number => {
        if (segments.length === 0) return 0

        // Need to handle the circular nature of angles
        // Convert to cartesian coordinates, average, then convert back to angle
        let sumX = 0
        let sumY = 0

        segments.forEach((segment) => {
            // Convert angle to radians
            // Note: midAngle in recharts is in degrees, with 0 at 12 o'clock position
            // and moving clockwise, we need to adjust for correct trigonometric calculation
            const adjustedAngle = 90 - segment.midAngle
            const radians = (adjustedAngle * Math.PI) / 180
            sumX += Math.cos(radians)
            sumY += Math.sin(radians)
        })

        // Get the average coordinates
        const avgX = sumX / segments.length
        const avgY = sumY / segments.length

        // Convert back to angle in degrees (atan2 returns angle in standard position)
        let avgAngle = Math.atan2(avgY, avgX) * (180 / Math.PI)

        // Convert from standard position (0° at right, counterclockwise)
        // to recharts angle (0° at top, clockwise)
        avgAngle = 90 - avgAngle

        // Normalize to 0-360 range
        while (avgAngle < 0) avgAngle += 360
        while (avgAngle >= 360) avgAngle -= 360

        return avgAngle
    }

    // Calculate the total percentage of small segments
    const calculateTotalPercentage = (segments: PieData[]): string => {
        const segmentTotal = segments.reduce((acc, segment) => acc + segment.percentage, 0)
        return segmentTotal.toFixed(1)
    }

    // Render aggregated label for small segments
    const renderAggregatedLabel = () => {
        // Skip rendering during transitions or with insufficient segments
        if (smallSegments.length <= 1 || transitioning) {
            return null
        }

        // Calculate the average angle of all small segments
        const avgMidAngle = calculateAverageAngle(smallSegments)

        // Use the first segment's data for common values
        const { cx, cy, outerRadius } = smallSegments[0]

        // Calculate position for the line end points using the average angle
        const sin = Math.sin((-avgMidAngle * Math.PI) / 180)
        const cos = Math.cos((-avgMidAngle * Math.PI) / 180)

        // Start point of the line (near the pie)
        const startX = cx + (outerRadius + 5) * cos
        const startY = cy + (outerRadius + 5) * sin

        // End point of the line (where the text will be)
        const textAnchor = cos >= 0 ? 'start' : 'end'
        const lineLength = 35 // Slightly longer than regular labels
        const endX = startX + lineLength * cos
        const endY = startY + lineLength * sin

        const textX = endX + (cos >= 0 ? 5 : -5)
        const totalPercentage = calculateTotalPercentage(smallSegments)

        // Sort segments by percentage (descending) for more readable output
        const sortedSegments = [...smallSegments].sort(
            (a, b) => Number(b.percentage) - Number(a.percentage),
        )

        return (
            <g
                className={`aggregated-labels ${isHoveringOtherSegments ? 'hover' : ''} animate__animated animate__fadeIn`}
                onMouseEnter={() => setIsHoveringOtherSegments(true)}
                onMouseLeave={() => setIsHoveringOtherSegments(false)}
            >
                <line
                    x1={startX}
                    y1={startY}
                    x2={endX}
                    y2={endY}
                    stroke="#666"
                    strokeWidth={1}
                />
                <text
                    x={textX}
                    y={endY}
                    fill={darkenColor('#666', 25)}
                    textAnchor={textAnchor}
                    dominantBaseline="central"
                    className="aggregated-label"
                >
                    <tspan
                        x={textX}
                        dy="0"
                        fontWeight="600"
                        style={chartLabelStyle}
                        className="aggregate-label-title"
                    >
                        {`Other Segments: ${totalPercentage}%`}
                    </tspan>

                    {isHoveringOtherSegments && sortedSegments.map((segment, i) => {
                        const segmentColor = COLORS[segment.index % COLORS.length]
                        const segmentLabelColor = darkenColor(segmentColor, 30)
                        
                        return (
                            <tspan
                                key={`small-${i}`}
                                x={textX}
                                dy="1.2em"
                                fill={segmentLabelColor}
                                className="aggregated-segment"
                                style={chartLabelStyle}
                            >
                                {`${segment.name}: ${segment.percentage.toFixed(1)}% (${segment.value || 0})`}
                            </tspan>
                        )
                    })}
                </text>
            </g>
        )
    }

    return (
        <AnalyticsChartWrapper title={title} className={`analytics-pie-chart ${className}${onPointClick ? ' drillable' : ''}`}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={processedData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={'70%'}
                        innerRadius={'30%'}
                        fill="#8884d8"
                        dataKey={valueKey}
                        onClick={(entry: any) => {
                            if (!onPointClick) {
                                return
                            }
                            const row = entry?.payload || entry
                            const labelValue = row?.[labelKey]
                            if (labelValue === undefined || labelValue === null) {
                                return
                            }
                            onPointClick({
                                chartType: 'pie',
                                labelKey,
                                labelValue,
                                row,
                            })
                        }}
                    >
                        {processedData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip content={customTooltip} />
                    {smallSegments.length > 1 && renderAggregatedLabel()}
                </PieChart>
            </ResponsiveContainer>
        </AnalyticsChartWrapper>
    )
}
